import React, {ErrorInfo, PropsWithChildren} from "react";
import Sentry from "lib/sentry";
import {ErrorFallbackUI} from "components/ErrorFallbackUI";

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

type AppErrorBoundaryProps = PropsWithChildren<{
  /** @default true */
  fullPage?: boolean;
  /** @default true */
  captureSentry?: boolean;
}>;

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {hasError: true, error, errorInfo: null};
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({errorInfo});

    if (this.props.captureSentry) {
      Sentry.withScope(scope => {
        scope.setTag("boundary_scope", "app-error-boundary");
        scope.setExtra("component_stack", errorInfo.componentStack);
        Sentry.captureException(error);
      });
    }
  }

  private resetErrorBoundary = () => {
    this.setState({hasError: false, error: null, errorInfo: null});
    window.scrollTo(0, 0);
  };

  public render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallbackUI
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
          fullPage={this.props.fullPage ?? true}
        />
      );
    }

    return this.props.children;
  }
}
